import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import Image from 'components/Common/Image/Image'
import Heading from 'LuxKit/Typography/Heading'
import RadioGroup from 'LuxKit/Radio/RadioGroup'
import styled from 'styled-components'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import Clickable from 'components/Common/Clickable'
import TextLink from 'LuxKit/TextLink'
interface Props {
  gridComponents: App.ContentfulFormGridComponents
  /**
   * Name used for the radio inputs so the form parser can capture the value
   */
  name?: string
}

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(12)};
  row-gap: ${rem(16)};
  ${mediaQueryUp.tablet} {
    flex-direction: row;
    grid-template-columns: repeat(3, 1fr);
    align-items: start;
  }
`

const Tile = styled(Clickable)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};
  padding: ${rem(8)};
  cursor: pointer;
  user-select: none;

  &.selected {
    border-color: ${props => props.theme.palette.brand.primary.normal};
    box-shadow: ${props => props.theme.shadow.bottom.medium};
    background-color: white;
  }
`

export default function ContentfulImageButtonGroup(props: Props) {
  const intl = useIntl()
  const { gridComponents, name = 'image-button-group' } = props
  const [selectedOption, setSelectedOption] = useState<string | null>(gridComponents[0].title)

  const learnMoreText = intl.formatMessage({
    id: 'pttRthsgCxpIVub85wU8',
    defaultMessage: 'Learn more',
    description: 'Fallback link text for image button group when linkText is not provided',
  })

  return <RadioGroup>
    {selectedOption && <input type="hidden" name={name} value={selectedOption} />}
    <Container role="radiogroup" aria-label={name}>
      {gridComponents.map((component, index) => {
        const isSelected = component.title === selectedOption
        const onSelect = () => setSelectedOption(component.title)
        const onKeyDown: React.KeyboardEventHandler<HTMLButtonElement> = (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            onSelect()
          }
        }

        return (
          <Tile
            key={index}
            className={isSelected ? 'selected' : undefined}
            role="radio"
            aria-checked={isSelected}
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={onKeyDown}
          >
            <div>
              <Image image={component.image} fit="center" />
            </div>
            <Heading variant="heading6" align="center">{component.title}</Heading>
            {component.linkTo && <TextLink weight="bold" size="medium" underline href={component.linkTo} target="_blank">{component.linkText || learnMoreText}</TextLink>}
          </Tile>
        )
      })}
    </Container>
  </RadioGroup>
}
